/**
 * Arms the audio graph on the first user gesture anywhere in the app.
 *
 * Mounts once, at the root. Not on the live screen: the tap that gets you to the
 * table happens on the join screen, so listeners mounted in `LivePlay` would
 * arrive *after* the only gesture there has been so far, and the first round
 * would be silent.
 */

import { useEffect, useState } from 'react';
import { audioState, primeAudio, watchAudioState, type AudioState } from '@/lib/audio/context';

/** Any of these counts as the gesture that lets audio start. */
const GESTURES = ['pointerdown', 'touchend', 'mousedown', 'keydown', 'click'] as const;

export function useAudioUnlock(): void {
  useEffect(() => {
    const arm = () => primeAudio();

    // Deliberately NOT `once`. iOS can drop the context back to suspended after
    // a phone call, a Siri invocation, or a switch to a Bluetooth device, and the
    // next tap has to be able to fix it. primeAudio() bails on one comparison
    // when it is already running, so leaving these installed costs nothing.
    for (const type of GESTURES) {
      document.addEventListener(type, arm, { capture: true, passive: true });
    }

    // Coming back from the background: best-effort. On iOS a resume without a
    // fresh gesture is often ignored, which is exactly why the listeners above
    // stay armed.
    const onVisible = () => {
      if (document.visibilityState === 'visible') primeAudio();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      for (const type of GESTURES) {
        document.removeEventListener(type, arm, { capture: true });
      }
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}

/** The current audio state, for the mute button to reflect. */
export function useAudioState(): AudioState {
  const [state, setState] = useState<AudioState>(() => audioState());

  useEffect(() => {
    const update = () => setState(audioState());
    watchAudioState(update);
    update();
    return () => watchAudioState(null);
  }, []);

  return state;
}
